import { clsx } from 'clsx'

interface SwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  /** Accessible name when there is no visible label to point at. */
  label?: string
  /** The `id` of a visible label element — preferred over `label`. */
  labelledBy?: string
  /** The `id` of a visible description element. */
  describedBy?: string
  id?: string
  disabled?: boolean
  className?: string
}

/**
 * Accessible on/off toggle. Gold track when on, neutral when off. Use for
 * boolean settings like contact-field visibility.
 *
 * Give it a visible label via `labelledBy` where one exists; `aria-label` on
 * its own hides the relationship between the switch and the words next to it.
 */
export function Switch({
  checked,
  onChange,
  label,
  labelledBy,
  describedBy,
  id,
  disabled,
  className,
}: SwitchProps) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={clsx(
        'relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full ring-1 ring-inset ring-black/5 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-border-hover',
        checked ? 'bg-brand-primary' : 'bg-border-default',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      <span
        className={clsx(
          'inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-1 ring-black/5 transition-transform duration-150',
          checked ? 'translate-x-[22px]' : 'translate-x-0.5'
        )}
      />
    </button>
  )
}
